import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {Collapse, Table, Icon, Select, Row, Col, message} from 'antd';
import {injectIntl, FormattedMessage} from 'react-intl';
import { graphql } from 'react-apollo';
import {LEADS_QUERY} from "./gql/query";
import { Route } from "react-router-dom";

import copy from '../../utils/copyClipboard';
import { linesToParagraphs } from '../../landing/common/Functions';
import { getFraudMessage } from '../../utils/isFraud';
import { Svg } from '../../utils/Svg';
import { getLeadQuality } from './utils';

const Panel = Collapse.Panel;
const Option = Select.Option;

const DEFAULT_PAGINATION =  {
  pageSize: 10,
  current: 1,
  pageSizeOptions: ["10", "30", "50", "100"],
  showSizeChanger: false,
  itemRender: (current, type, originalElement) => {
    if (type === 'prev') {
      return <a className='ant-pagination-item-link'>
        <Icon type="left" style={{ fontWeight: 'bold' }} />
        <FormattedMessage id='analytics.prev' defaultMessage='Prev'/>
      </a>;
    } else if (type === 'next') {
      return <a className='ant-pagination-item-link'>
        <FormattedMessage id='analytics.next' defaultMessage='Next'/>
        <Icon type="right" style={{ fontWeight: 'bold' }} />
      </a>;
    }
    return originalElement;
  }
}


class MainTable extends React.Component {

  static propTypes = {
    dateFrom: PropTypes.object.isRequired,
    dateTo: PropTypes.object.isRequired,
    groups: PropTypes.array,
    projects: PropTypes.array,
    labelType: PropTypes.string,
    labelValues: PropTypes.object,
  }

  state = {
    value: ''
  }

  onTableChange = (pagination, filters, sorter) => {
    this.props.onTableChange({currentPage: pagination.current, pageSize: pagination.pageSize})
  }

  onPageSizeChange = (pageSize) => {
    let {currentPage} = this.props;
    this.props.onTableChange({currentPage, pageSize})
  }

  getColumnSorted(order, field) {
    if (!order) { return false; }
    let f = order;
    let asc = 'ascend';
    if (order[0] === '-') {
      f = order.substr(1);
      asc = 'descend';
    }
    return f === field && asc;
  }

  _handleSortChange = (field, pagination) => {
    const order = this._getSortOrder(field);
    this.props.onTableChange(pagination, null, {field, columnKey: field, order })
  }

  _getSortOrder = field => '-' + field ===  this.props.order ? 'ascend': 'descend'

  _onCopy = (e, value) => {
    e.stopPropagation()
    this.setState({value})
    copy(value)
    message.success(
      this.props.intl.formatMessage({id: 'analytics.copied', defaultMessage: 'Copied'}), 1
    )
  }


  // Hightlihgt even rows, and fraud leads
  _rowClassName = ({isFraud}, index) => 
    (isFraud? 'fraud_highlight ': ' ') + (index % 2 === 0 ? 'odd': 'even')

  //Hightlight mobile and tablet
  _getMobileClass = deviceModel => deviceModel && this._isMobile(deviceModel.deviceType)? 'mobile_highlight': ''
  
  _isMobile = deviceType => deviceType && ~['Phone','Tablet'].indexOf(deviceType.name)

  render () {
    const { formatMessage } = this.props.intl;
    const {order, pageSize, currentPage} = this.props;
    let { loading, leads } = this.props.data;

    let pagination = {...DEFAULT_PAGINATION};
    if (pageSize) {
      pagination.pageSize = parseInt(pageSize, 10);
    }
    if (currentPage) {
      pagination.current = parseInt(currentPage, 10);
    }
    pagination.total = leads && leads.total;
    const columns = [
      {
        title: (
          <div 
            className="header__title" 
            onClick={this._handleSortChange.bind(this, 'id', pagination)}>
              {'ID'} 
          </div>),
        dataIndex: 'id',
        key: 'id',
        render: (text) => {
          return (
            <div onClick={e => this._onCopy(e, text)} className={this.state.value === text? "id_copied": ""}>
              <button
                className="copy_button"
                title={text}
                style={{ maxWidth: '60px' }}
              >{text}
              </button>
              <button 
                className="copy_button" 
                title={formatMessage({
                  id: 'analytics.copy-title',
                  defaultMessage: 'Copy to clipboard'
                })}
              >
                <Svg id="copy"/>
              </button>
            </div>
          )
        },
        sorter: true,
        sortOrder: this.getColumnSorted(order, 'id')
      },
      {
        title: (
          <div 
            className="header__title" 
            onClick={this._handleSortChange.bind(this, 'submitTime', pagination)}>
              {formatMessage({id: 'analytics.date', defaultMessage: 'Date'})} 
          </div>),
        dataIndex: 'submitTime',
        key: 'submitTime',
        render: (text) => {
          return <div
            title={text}
            style={{ maxWidth: '55px' }}
          >{moment.utc(text).format(formatMessage({
              id: 'analytics.date-format',
              defaultMessage: 'YY.MM.DD'
            }))}</div>
        },
        sorter: true,
        sortOrder: this.getColumnSorted(order, 'submitTime')
      },
      {
        title: (
          <div 
            className="header__title" 
            onClick={this._handleSortChange.bind(this, 'deviceName', pagination)}>
              {formatMessage({id: 'analytics.device', defaultMessage: 'Device'})} 
          </div>),
        dataIndex: 'deviceName',
        key: 'deviceName',
        render: (text, {deviceModel}) => {
          return <div
            className={this._getMobileClass(deviceModel)}
            title={text}
            style={{ maxWidth: '55px' }}
          >{text}</div>
        },
        sorter: true,
        sortOrder: this.getColumnSorted(order, 'deviceName')
      },
      {
        title: (
          <div 
            className="header__title" 
            onClick={this._handleSortChange.bind(this, 'osGroup', pagination)}>
              {formatMessage({id: 'analytics.os', defaultMessage: 'OS'})} 
          </div>),
        dataIndex: 'osGroup',
        key: 'osGroup',
        render: (text, {deviceModel}) => {
          return <div
            className={this._getMobileClass(deviceModel)}
            title={text}
            style={{ maxWidth: '55px' }}
          >{text}</div>
        },
        sorter: true,
        sortOrder: this.getColumnSorted(order, 'osGroup')
      },
      {
        title: (
          <div 
            className="header__title" 
            onClick={this._handleSortChange.bind(this, 'cityOrRegion', pagination)}>
              {formatMessage({id: "analytics.city", defaultMessage: 'City'})} 
          </div>),
        dataIndex: 'cityOrRegion',
        key: 'cityOrRegion',
        render: (text) => {
          return <div
            title={text}
            style={{ maxWidth: '78px' }}
          >{text}</div>
        },
        sorter: true,
        sortOrder: this.getColumnSorted(order, 'cityOrRegion')
      },
      {
        title: (
          <div 
            className="header__title" 
            onClick={this._handleSortChange.bind(this, 'countryName', pagination)}>
              {formatMessage({id: "analytics.country", defaultMessage: 'Country'})} 
          </div>),
        dataIndex: 'countryName',
        key: 'countryName',
        render: (text) => {
          return <div
            title={text}
            style={{ maxWidth: '78px' }}
          >{text}</div>
        },
        sorter: true,
        sortOrder: this.getColumnSorted(order, 'countryName')
      },
      {
        title: (
          <div 
            className="header__title" 
            onClick={this._handleSortChange.bind(this, 'trafficChannel.name', pagination)}>
                <span>
                  <FormattedMessage id="analytics.thead-traffic" defaultMessage='Traffic channel'>
                    {linesToParagraphs}
                  </FormattedMessage>
                </span> 
          </div>),
        dataIndex: 'trafficChannel.name',
        key: 'trafficChannel.name',
        render: (text) => {
          return <div
            title={text}
            style={{ maxWidth: '90px' }}
          >{text}</div>
        },
        sorter: true,
        sortOrder: this.getColumnSorted(order, 'trafficChannel.name')
      },
      {
        title: (
          <div 
            className="header__title" 
            onClick={this._handleSortChange.bind(this, 'affiliateNetwork.nameEng', pagination)}>
              <span>
                <FormattedMessage id="analytics.thead-affiliateNetwork" defaultMessage='Affiliate network'>
                  {linesToParagraphs}
                </FormattedMessage>
              </span>
          </div>),
        dataIndex: 'affiliateNetwork.nameEng',
        key: 'affiliateNetwork.nameEng',
        render: (text) => {
          return <div
            title={text}
            style={{ maxWidth: '55px' }}
          >{text}</div>
        },
        sorter: true,
        sortOrder: this.getColumnSorted(order, 'affiliateNetwork.nameEng')
      },
      {
        title: (
          <div 
            className="header__title" 
            onClick={this._handleSortChange.bind(this, 'affiliateNetwork.nameRus', pagination)}>
              <span>
                <FormattedMessage id="analytics.thead-affiliate" defaultMessage='Affiliate'>
                  {linesToParagraphs}
                </FormattedMessage>
              </span>
          </div>),
        dataIndex: 'affiliateNetwork.nameRus',
        key: 'affiliateNetwork.nameRus',
        render: (text) => {
          return <div
            title={text}
            style={{ maxWidth: '55px' }}
          >{text}</div>
        },
        sorter: true,
        sortOrder: this.getColumnSorted(order, 'affiliateNetwork.nameRus')
      },
      {
        title: (
          <div 
            className="header__title" 
            onClick={this._handleSortChange.bind(this, 'score', pagination)}>
              {formatMessage({id: "analytics.thead-score", defaultMessage: 'Score'})} 
          </div>),
        dataIndex: 'score',
        key: 'score',
        render: (text) => {
          return <div
            className={`fraud-field__score score_${getLeadQuality(text)}`}
          >{Math.round(text * 100)}</div>
        },
        sorter: true,
        sortOrder: this.getColumnSorted(order, 'score')
      },
      {
        title: formatMessage({id: "analytics.thead-fraud", defaultMessage: 'Fraud'}),
        dataIndex: 'fraud_code',
        key: 'fraud_code',
        render: (text) => {
          return <div
            className="fraud-field"
            style={{ maxWidth: '90px' }}
          >{getFraudMessage(text)}</div>
        },
      },
    ];
    // resize table
    const screenWidth = window.screen.availWidth;
    const tableMaxWidth = screenWidth - ( screenWidth > 767 && screenWidth < 900 ? 220 : 0) - 40;
    let dataSource = leads && leads.data && leads.data.map(l => {

      let _l = {
        ...l,
        fraud_code: l.isFraud ? {
          age: l.fraudAgeCheckOk,
          duration: l.fraudDurationCheckOk,
          authenticity: l.fraudAuthenticityCheckOk,
        }: undefined,
      };
      return _l
    });
    return (
      <Collapse defaultActiveKey={['main-table']} className="main-table" style={this.props.style}>
        <Panel
          header={
            <span className='collapse-title'>
              <FormattedMessage id='analytics.conversion' defaultMessage='Conversions'/>
            </span>}
          key="main-table"
        >
          <Row style={{padding: '15px 0 24px'}}>
            <Col span={12}>
              <label style={{fontSize: '14px', fontWeight: 'bold'}}>
                <FormattedMessage id='analytics.show-on-page' defaultMessage='Show:'/>
                <Select
                  style={{fontSize: '14px', marginLeft: '8px'}}
                  size={'large'}
                  onChange={this.onPageSizeChange}
                  value={pagination.pageSize + ''}
                >
                  {DEFAULT_PAGINATION.pageSizeOptions.map((v) => {
                    return <Option key={v} value={v} >{v}</Option>;
                  })}
                </Select>
              </label>
            </Col>
            <Col span={12} style={{textAlign: 'right'}}>
              <label style={{fontSize: '14px', fontWeight: 'bold'}}>
                <FormattedMessage
                  id='analytics.leads-found'
                  defaultMessage="Found {total, plural,\none {# lead}\nother {# leads}\n}"
                  values={{total: leads && leads.total}}
                />
              </label>
            </Col>
          </Row>
          <Route render={({history}) => (
            <Table
              ref={'leadsTable'}
              className={'leads-table'}
              style={{maxWidth: tableMaxWidth}}
              bordered
              rowClassName={this._rowClassName}
              rowKey='id'
              columns={columns}
              loading={loading}
              dataSource={dataSource}
              pagination={pagination}
              onChange={this.props.onTableChange}
              onRow={({id}) => ({
                onClick: () => {
                  // Open the lead-information in new tab with fallback to native redirect
                  if(!window.open(`analytics/lead?id=${id}`))
                    history.push(`/dashboard/analytics/lead?id=${id}`)
                }
              })}
            />
          )}/>
        </Panel>
      </Collapse>
    )
  }
}

export default graphql(LEADS_QUERY, {
  options: ({
    dateFrom, dateTo, projects, bySubnet, labelType, labelValues, order = '-submitTime', region,
    osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities, pageSize, currentPage
  }) => {
    pageSize = pageSize || DEFAULT_PAGINATION.pageSize;
    currentPage = currentPage || DEFAULT_PAGINATION.current;
    return {
      variables: {
        dateFrom, dateTo, projects, bySubnet, labelType, labelValues, region,
        osGroups, browserGroups, trafficChannels, affiliateNetworks, deviceTypes, country, cities,
        limit: pageSize, offset: pageSize * (currentPage - 1), order
      },
      notifyOnNetworkStatusChange: true
    };
  }
})(injectIntl(MainTable));



// WEBPACK FOOTER //
// ./src/components/dashboard/analytics/main-table.js